import { InteractiveTutorial, InteractiveTutorialProps } from "@/features/interactive_tutorial";
import { Alert, Image, Space, TourStepProps } from "antd";
export const LargeSiteScreenTutorial = (props: InteractiveTutorialProps<Record<any, any>>) => {
  const stepProps = {
    nextButtonProps: {
      className: "ant-btn-lg"
    },
    prevButtonProps: {
      className: "ant-btn-lg"
    }
  };
  const steps: TourStepProps[] = [{
    title: "1. Launch the Tool",
    description: <Space direction="vertical">
          <ul className="mb-2 list-inside list-disc pl-4">
            <li>From the Screening Tools menu, select Large Site Screen.</li>
            <li>On the tool’s home page, click “Start New Run.”</li>
          </ul>
          <div className="flex w-full justify-center">
            <Image alt="Indication to click the button titled 'Analyse a Large Site'." src="/img/tours/large-site-screen/launch.png" />
          </div>
        </Space>,
    ...stepProps
  }, {
    title: "2. Locate Your Site",
    description: <Space direction="vertical">
          <p className="mb-2">Use the address search bar or map navigation tools to find your site</p>
          <div className="flex w-full justify-center">
            <Image alt="Indication to find site using the address search bar or map navigation tools." src="/img/tours/large-site-screen/locate_a.png" />
          </div>
        </Space>,
    ...stepProps
  }, {
    title: "3. Draw Your Site Boundary",
    description: <Space direction="vertical" className="max-h-[80vh] overflow-y-scroll">
          <p className="mb-2">When ready, select “Draw Polygon” from the left-hand map toolbar.</p>
          <div className="flex w-full justify-center">
            <Image alt="Indication to select 'Draw Polygon' from the left-hand map toolbar." src="/img/tours/large-site-screen/locate_b.png" />
          </div>
          <p className="mb-2">
            Click on the map to begin drawing your polygon. Continue clicking to add boundary markers.
          </p>
          <div className="flex w-full justify-center">
            <Image alt="Indication to click on the map to begin drawing polygon." src="/img/tours/large-site-screen/draw_boundary_a.png" />
          </div>
          <p className="mb-2">To complete the shape, click back on the first boundary marker.</p>
          <div className="flex w-full justify-center">
            <Image alt="Indication to click on the map to begin drawing polygon." src="/img/tours/large-site-screen/draw_boundary_complete.png" />
          </div>
        </Space>,
    ...stepProps
  }, {
    title: "3. Draw Your Site Boundary - Editing Options",
    description: <Space direction="vertical" className="max-h-[80vh] overflow-y-scroll">
          <ul className="mb-2 list-inside list-disc pl-4">
            <li>
              <strong>Edit:</strong> Click “Edit,” drag boundary markers to adjust, then click “Save” or “Cancel.”
            </li>
            <li>
              <strong>Delete:</strong> Click “Delete,” select the polygon, then “Save” to confirm.
            </li>
          </ul>
          <div className="flex w-full justify-center">
            <Image alt="Indication to adjust or delete polygon." src="/img/tours/large-site-screen/draw_boundary_edit_b.png" />
          </div>

          <Alert type="error" message="
            The total area of your large site cannot exceed 1km². If your polygon exceeds this limit is will turn red
            and you will be unable to proceed." />
          <div className="flex w-full justify-center">
            <Image alt="Tool tip indicating the total area of the large site." src="/img/tours/large-site-screen/draw_boundary_fail_c.png" />
          </div>
          <p className="mb-2">
            Once you&apos;ve finished editing, enter a name for the polygon, then click “Generate Grid Points” to
            proceed to the next step.
          </p>
          <div className="flex w-full justify-center">
            <Image alt="Indication to enter a name for the polygon and click 'Generate Grid Points' to proceed." src="/img/tours/large-site-screen/draw_boundary_success_d.png" />
          </div>
        </Space>,
    ...stepProps
  }, {
    title: "4. Generate Grid Points",
    description: <Space direction="vertical">
          <ul className="mb-2 list-inside list-disc pl-4">
            <li>The tool will create a grid with 20 m² spacing by default.</li>
          </ul>
          <Alert type="error" message="Each Large Site Screen can have a maximum of 2,500 grid points. If exceeded, the
            point counter turns red and you will be unable to proceed." />
          <div className="flex w-full justify-center">
            <Image alt="Tool tip indicating the maximum number of grid points." src="/img/tours/large-site-screen/generate_grid_a.png" />
          </div>
        </Space>,
    ...stepProps
  }, {
    title: "4. Generate Grid Points - Editing Options",
    description: <Space direction="vertical" className="max-h-[80vh] overflow-y-scroll">
          <ul className="mb-2 list-inside list-disc pl-4">
            <li>
              <strong>Add:</strong> Click inside the polygon to add more points manually.
            </li>
            <li>
              <strong>Move:</strong> Click and drag a point to reposition.
            </li>
            <li>
              <strong>Delete:</strong> Click a point, then select “Delete.”
            </li>
          </ul>
          <div className="flex w-full justify-center">
            <Image alt="Tool tip indicating the maximum number of grid points." src="/img/tours/large-site-screen/generate_grid_b_edit.png" />
          </div>
          <Alert type="error" message="All grid points must be within the boundary of a polygon. If a grid point is located outside of your polygon you will receive an error and you will be unable to proceed. " />
          <div className="flex w-full justify-center">
            <Image alt="Tool tip indicating the maximum number of grid points." src="/img/tours/large-site-screen/generate_grid_c_delete.png" />
          </div>
          <ul className="mb-2 list-inside list-disc pl-4">
            <li>
              Once you&apos;ve finished editing your grid points, click “Confirm Asset Settings” to proceed to the next
              step.
            </li>
          </ul>
          <Alert type="warning" message="The presence of water will skew results for your Large Site. Remove all grid points that appear within bodies of water." />
          <div className="flex w-full justify-center">
            <Image alt="Tool tip indicating the maximum number of grid points." src="/img/tours/large-site-screen/generate_grid_d_water.png" />
          </div>
        </Space>,
    ...stepProps
  }, {
    title: "5. Define Asset & Financial Settings",
    description: <Space direction="vertical" className="max-h-[80vh] overflow-y-scroll">
          <p className="mb-2">
            <strong>Asset Settings:</strong> Select the archetype, build year, and estimated floor height for the real
            or hypothetical asset at the location.
          </p>
          <Alert type="warning" message="These settings will apply to all grid points in the polygon." />
          <div className="flex w-full justify-center">
            <Image alt="Tool tip indicating the maximum number of grid points." src="/img/tours/large-site-screen/define_asset_setting.png" />
          </div>
          <p className="mb-2">
            <strong>Financial Settings:</strong> Enter estimated market value and replacement cost for the asset at that
            location. These values can be entered either:
          </p>
          <ul className="mb-2 list-inside list-disc pl-4">
            <li>As totals for the whole site, or</li>
            <li>Per individual grid point</li>
          </ul>
          <Alert type="warning" message="Per-point values will be multiplied across your grid for a total site estimate." />
          <div className="flex w-full justify-center">
            <Image alt="Tool tip indicating the maximum number of grid points." src="/img/tours/large-site-screen/define_financial_setting.png" />
          </div>
          <p className="mb-2">
            Click <strong>“Proceed to Review.”</strong>
          </p>
        </Space>,
    ...stepProps
  }, {
    title: "6. Review & Configure Analysis",
    description: <Space direction="vertical" className="max-h-[80vh] overflow-y-scroll">
          <p className="mb-2">
            Name your analysis and choose sharing permissions.
            <Alert type="warning" message="If sharing permissions are enabled, the results of your Large Site Screen will also be visible to other members of your Organisation’s group." />
          </p>
          <div className="flex w-full justify-center">
            <Image alt="Tool tip indicating the maximum number of grid points." src="/img/tours/large-site-screen/review_share_a.png" />
          </div>
          <p className="mb-2">
            Select the year and hazard(s) you want to use to define the riskiest grid point criteria
          </p>
          <div className="flex w-full justify-center">
            <Image alt="Tool tip indicating the maximum number of grid points." src="/img/tours/large-site-screen/review_hazard_b.png" />
          </div>
          <ul className="mb-2 list-inside list-disc pl-4">
            <li>Review your quota usage and remaining allocation.</li>
            <li>
              When ready, click “Run Analysis” to begin.
              <div className="flex w-full justify-center">
                <Image alt="Tool tip indicating the maximum number of grid points." src="/img/tours/large-site-screen/review_run_c.png" />
              </div>
            </li>
          </ul>
        </Space>,
    ...stepProps
  }, {
    title: "7. Processing Your Analysis",
    description: <Space direction="vertical">
          <ul className="mb-2 list-inside list-disc pl-4">
            <li>Your Large Site will take a moment to upload. Upload typically takes a few minutes.</li>
            <li>
              Once uploaded, your analysis will begin to process. Processing may take up to two hours depending on size.
            </li>
          </ul>
          <Alert type="warning" showIcon message="Do not close your browser while the analysis is running." />
          <div className="flex w-full justify-center">
            <Image alt="Tool tip indicating the maximum number of grid points." src="/img/tours/large-site-screen/processing.png" />
          </div>
        </Space>,
    ...stepProps
  }, {
    title: "8. Accessing Your Results",
    description: <Space direction="vertical">
          <ul className="mb-2 list-inside list-disc pl-4">
            <li>Once complete, your run will appear on the tool dashboard.</li>
            <li>Download results using the export options next to your analysis record.</li>
          </ul>
          <Alert type="warning" showIcon message="Errors will be shown next to the run, marked with a hazard icon." />
          <div className="flex w-full justify-center">
            <Image alt="Tool tip indicating the maximum number of grid points." src="/img/tours/large-site-screen/results.png" />
          </div>
        </Space>,
    ...stepProps
  }];
  return <InteractiveTutorial<Record<any, any>> isWide={true} steps={steps} {...props} data-sentry-element="InteractiveTutorial" data-sentry-component="LargeSiteScreenTutorial" data-sentry-source-file="LargeSiteScreenTutorial.tsx" />;
};